<script>
import KeyValueResult from "@/components/InvestigationEngine/Results/KeyValueResult.vue";
import TableResult from "@/components/InvestigationEngine/Results/TableResult.vue";
import LoadingState from "@/components/InvestigationEngine/States/Loading.vue";
import ErrorState from "@/components/InvestigationEngine/States/Error.vue";
import EmptyState from "@/components/InvestigationEngine/States/Empty.vue";
import axios from "axios";

export default {
    components: {
        EmptyState,
        KeyValueResult,
        TableResult,
        LoadingState,
        ErrorState
    },

    inject: {
        onCheckComplete: { default: null },
    },

    props: {
        serial: String,
        result: Object,
    },

    data() {
        return {
            check: this.result,
            copySuccess: false,
            isCollapsed: false
        }
    },

    computed: {
        checkResults() {
            if (!this.check.data) return [];
            return Array.isArray(this.check.data) ? this.check.data : [this.check.data];
        },

        visibleResults() {
            return this.checkResults.filter(resultData => {
                if (resultData.type === 'key_value') {
                    const filteredItems = Object.fromEntries(
                        Object.entries(resultData.items || {}).filter(([key, value]) =>
                            value !== null && value !== undefined && value !== '' && value !== 'N/A'
                        )
                    );
                    return Object.keys(filteredItems).length > 0;
                }

                if (resultData.type === 'table') {
                    return resultData.rows && resultData.rows.length > 0;
                }

                return true;
            });
        },

        hasCopyableContent() {
            if (!this.check.data || this.check.status !== 'completed') {
                return false;
            }

            return this.check.data.some(result => result.copyable && result.copyable.plain_text);
        }
    },

    async mounted() {
        if (this.check.status === 'pending') {
            await this.loadCheck(this.check);
        }
    },

    methods: {
        async loadCheck(check) {
            try {
                let response;

                let url = `/api-internal/investigation-engine/${this.serial}/check/${check.id}`;

                if (check.scope === 'meter' && check.meter_serial) {
                    url += `?meter=${check.meter_serial}`;
                }
                else if (check.serial) {
                    url += `?device=${check.serial}`;
                }

                response = await axios.get(url);

                check.status = 'completed';
                check.data = response.data.data;

            } catch (error) {
                check.status = 'failed';
                check.error = error.response?.data?.error || error.message;
            } finally {
                if (typeof this.onCheckComplete === 'function') {
                    this.onCheckComplete({
                        id: check.id,
                        meter_serial: check.meter_serial ?? null,
                        status: check.status,
                        data: check.data ?? null,
                        error: check.error ?? null,
                    });
                }
            }
        },

        async copyPlainText() {
            try {
                let textToCopy = '';

                this.check.data.forEach(result => {
                    if (result.copyable && result.copyable.plain_text) {
                        textToCopy += result.copyable.plain_text + '\n\n';
                    }
                });

                const header = `[${this.check.title}]\n\n`;

                textToCopy = header + textToCopy;

                await navigator.clipboard.writeText(textToCopy);

                this.copySuccess = true;

                setTimeout(() => {
                    this.copySuccess = false;
                }, 2000);

            } catch (err) {
                console.error('Failed to copy:', err);
            }
        },

        toggleCollapsed() {
            this.isCollapsed = !this.isCollapsed;
        }
    }
}
</script>

<template>
    <div>
        <div
            @click="toggleCollapsed"
            :class="['flex justify-between items-start cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded transition-colors duration-200', isCollapsed ? '-mb-2' : 'mb-6']"
        >
            <div class="flex items-center">
                <svg
                    :class="['w-4 h-4 mr-3 transition-transform duration-200 text-gray-400', isCollapsed ? '' : 'rotate-90']"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>

                <div>
                    <h3 class="text-left text-gray-900 font-semibold m-0 p-0 text-xl tracking-tight">
                        {{ check.title }}

                        <span v-if="check.scope === 'meter' && check.meter_serial"
                              class="text-sm font-normal text-gray-500 ml-2">
                            ({{ check.meter_serial }})
                        </span>
                    </h3>
                </div>
            </div>

            <div class="flex items-center space-x-2">
                <button
                    v-if="!isCollapsed && hasCopyableContent"
                    @click.stop="copyPlainText"
                    class="px-3 py-1 text-sm hover:bg-gray-100 text-gray-500 rounded transition-colors duration-200"
                >
                    {{ copySuccess ? 'Copied' : 'Copy' }}
                </button>
            </div>
        </div>

        <div v-if="!isCollapsed">
            <LoadingState
                v-if="check.status === 'pending'"
                :message="`Loading ${check.title}${check.meter_serial ? ' for ' + check.meter_serial : ''}`"
            />

            <EmptyState v-else-if="check.status === 'completed' && visibleResults.length === 0" />

            <div v-else-if="check.status === 'completed'" class="space-y-10">
                <div v-for="(resultData, idx) in visibleResults" :key="idx">

                    <KeyValueResult
                        v-if="resultData.type === 'key_value'"
                        :items="resultData.items"
                        :links="resultData.links || {}"
                        :statuses="resultData.statuses || {}"
                    />

                    <TableResult
                        v-else-if="resultData.type === 'table'"
                        :title="resultData.title"
                        :columns="resultData.columns"
                        :rows="resultData.rows"
                        :evaluated-statuses="resultData.evaluatedStatuses || {}"
                        :links="resultData.links || {}"
                        :sort-keys="resultData.sortKeys || {}"
                        :default-sort="resultData.defaultSort || null"
                    />

                    <div v-else>
                        <pre class="text-sm">{{ resultData }}</pre>
                    </div>
                </div>
            </div>

            <ErrorState
                v-else-if="check.status === 'failed'"
                :error="check.error"
            />

        </div>
    </div>
</template>